import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Target, Plus, Trash, PencilSimple } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const GoalTracking = ({ canWrite, clients }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [form, setForm] = useState({ title: '', target_amount: '', period: 'monthly', client_id: '' });

  const fetchGoals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/goals`, { withCredentials: true });
      setGoals(data);
    } catch (err) { console.error(err.message);
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      target_amount: parseFloat(form.target_amount),
      client_id: form.client_id || null
    };
    try {
      if (editGoal) {
        await axios.patch(`${BACKEND_URL}/api/goals/${editGoal.id}`, payload, { withCredentials: true });
        toast.success('Goal updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/goals`, payload, { withCredentials: true });
        toast.success('Goal created');
      }
      setAddOpen(false);
      setEditGoal(null);
      setForm({ title: '', target_amount: '', period: 'monthly', client_id: '' });
      fetchGoals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save goal');
    }
  };

  const handleDelete = async (goalId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/goals/${goalId}`, { withCredentials: true });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (err) { console.error(err.message);
      toast.error('Failed to delete goal');
    }
  };

  const openEdit = (goal) => {
    setEditGoal(goal);
    setForm({ title: goal.title, target_amount: String(goal.target_amount), period: goal.period, client_id: goal.client_id || '' });
    setAddOpen(true);
  };

  const getBarColor = (pct) => {
    if (pct >= 100) return 'bg-accent';
    if (pct >= 60) return 'bg-chart-4';
    return 'bg-destructive';
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading goals...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target size={24} weight="regular" />
          <div>
            <div className="text-xl font-semibold tracking-tight">Goal Tracking</div>
            <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">REVENUE TARGETS</div>
          </div>
        </div>
        {canWrite && (
          <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) { setEditGoal(null); setForm({ title: '', target_amount: '', period: 'monthly', client_id: '' }); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-sm" data-testid="add-goal-btn">
                <Plus size={16} weight="bold" className="mr-2" />
                Add Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="text-2xl tracking-tight">{editGoal ? 'Edit Goal' : 'New Goal'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Title *</Label>
                  <Input data-testid="goal-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Q1 Revenue Target" className="rounded-sm" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Target Amount (INR) *</Label>
                  <Input data-testid="goal-amount-input" type="number" step="0.01" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className="rounded-sm" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Period</Label>
                  <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="goal-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client (Optional)</Label>
                  <Select value={form.client_id || "__all__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__all__" ? "" : v })}>
                    <SelectTrigger className="rounded-sm" data-testid="goal-client-select">
                      <SelectValue placeholder="All clients (overall)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Clients (Overall)</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full rounded-sm" data-testid="submit-goal-btn">{editGoal ? 'Update Goal' : 'Create Goal'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {goals.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center text-muted-foreground">
          <Target size={48} weight="thin" className="mx-auto mb-4 opacity-50" />
          <p>No goals set yet. Create revenue targets to track your progress.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((goal) => {
            const clientName = goal.client_id ? clients.find(c => c.id === goal.client_id)?.name : 'All Clients';
            return (
              <div key={goal.id} data-testid={`goal-card-${goal.id}`} className="bg-card border border-border rounded-sm p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-sm">{goal.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 capitalize">{goal.period} · {clientName}</div>
                  </div>
                  {canWrite && (
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(goal)} className="p-1.5 hover:bg-muted rounded-sm transition-colors" data-testid={`edit-goal-${goal.id}`}>
                        <PencilSimple size={14} weight="regular" />
                      </button>
                      <button onClick={() => handleDelete(goal.id)} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-sm transition-colors" data-testid={`delete-goal-${goal.id}`}>
                        <Trash size={14} weight="regular" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-end justify-between mb-2">
                  <span className="text-2xl font-bold tracking-tight">₹{(goal.current_amount || 0).toLocaleString()}</span>
                  <span className="text-sm text-muted-foreground">/ ₹{goal.target_amount.toLocaleString()}</span>
                </div>

                <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getBarColor(goal.progress_pct)}`}
                    style={{ width: `${Math.min(goal.progress_pct, 100)}%` }}
                    data-testid={`goal-progress-${goal.id}`}
                  />
                </div>
                <div className="text-xs text-muted-foreground mt-1.5 text-right">{goal.progress_pct}%</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalTracking;
